import React, { useEffect, useMemo, useState } from 'react';

import { NavLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { MESSAGES } from '~/utils/message';
import { multiPageTemplates } from '~/utils/template-builder';
import { ENVELOPE_LEARN_URL, HAS_ENVELOPE } from '~/utils/constants';
import { balanceFormatter } from '~/utils/helperFunctions';
import { fetchUvCoatingPrice } from '~/redux/actions/products-actions';

// Components
import GenericAutocomplete from '~/components/General/Autocomplete';
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// MUI Components
import {
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Divider,
  Input,
  Grid,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  InputAdornment,
} from '@mui/material';

// Icons
import { Close as CloseIcon } from '@mui/icons-material';
import { InfoCIcon } from '~/assets/images';
// styles
import './stylesNew.scss';

const ActionsModal = ({
  open,
  onClose,
  selectedActionType,
  setSelectedActionType,
  editingStep,
  handleSaveAction,
  addingActionLoading,
  getActionTypeDetails,
  COMPAIGNS_ACTION_TYPE,
  templates,
  setSelectedTemplate,
  selectedTemplate,
  setSearch,
  setErrors,
  errors,
  loading,
  postageOptions,
  setSelectedPostage,
  selectedPostage,
  evelopeOptions,
  setSelectedEnvelope,
  selectedEnvelope,
  offerPercentageValue,
  handleOfferPercentageChange,
  handleDownloadMailerProof,
  handleDownloadEnvelopeProof,
  downloadingProof,
  downloadingEnvelope,
  validateAction,
  previouslySelectedActionType,
  setPreviouslySelectedActionType,
  previouslySelectedTemplate,
  setPreviouslySelectedTemplate,
  setOfferPercentageValue,
  uvCoating,
  setUvCoating,
}) => {
  const dispatch = useDispatch();
  const products = useSelector((state) => state.productReducers.products) || [];
  const [selectedPostCardOption, setSelectedPostCardOption] = useState(null);
  const [selectedRplOption, setSelectedRplOption] = useState(null);
  const [selectedProfessionalOption, setSelectedProfessionalOption] = useState(null);
  const [selectedSelfMailerOption, setSelectedSelfMailerOptionOption] = useState(null);
  const [uvCoatingPrice, setUvCoatingPrice] = useState(null);
  const [uvCoatingPriceLoading, setUvCoatingPriceLoading] = useState(false);

  const isUvCoatingVisible = selectedTemplate?.productType === 'Postcards';

  const handleUvCoatingToggle = () => {
    setUvCoating((currentValue) => !currentValue);
  };

  // Fetch the org's live UV coating rate whenever the selected template resolves to a postcard.
  useEffect(() => {
    if (!isUvCoatingVisible) {
      setUvCoatingPrice(null);
      setUvCoatingPriceLoading(false);
      return;
    }
    const matchedProductId = products.find(
      (product) =>
        product.productType === selectedTemplate?.productType &&
        product.paperSize === selectedTemplate?.postCardSize
    )?.id;
    if (!matchedProductId) {
      setUvCoatingPrice(null);
      setUvCoatingPriceLoading(false);
      return;
    }
    let cancelled = false;
    setUvCoatingPriceLoading(true);
    dispatch(fetchUvCoatingPrice(matchedProductId)).then((response) => {
      if (cancelled) return;
      const price = response?.data?.data?.uvCoatingPrice;
      setUvCoatingPrice(typeof price === 'number' ? price : null);
      setUvCoatingPriceLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isUvCoatingVisible, selectedTemplate?.productType, selectedTemplate?.postCardSize, products, dispatch]);

  // Reset the toggle if the user switches away from a postcard template.
  useEffect(() => {
    if (!isUvCoatingVisible && uvCoating) {
      setUvCoating(false);
    }
  }, [isUvCoatingVisible, uvCoating, setUvCoating]);

  const templateDropdownOptions = useMemo(
    () =>
      templates.map((template) => ({
        ...template,
        title: template.title || template.label,
      })),
    [templates]
  );

  const selectedTemplateDropdownValue = useMemo(
    () =>
      selectedTemplate
        ? {
            ...selectedTemplate,
            title: selectedTemplate.title || selectedTemplate.label,
          }
        : null,
    [selectedTemplate]
  );

  const handleTemplateSelection = (template) => {
    if (template?.id !== selectedTemplate?.id) {
      setSelectedPostage(null);
    }
    setSelectedTemplate(template);
    setErrors((currentErrors) => ({
      ...currentErrors,
      template: false,
    }));
  };

  const postcardOptions = [
    { id: '13', label: '4"x6"', type: 'send_postcard_4x6' },
    { id: '14', label: '6"x9"', type: 'send_postcard_6x9' },
    { id: '15', label: '6"x11"', type: 'send_postcard_6x11' },
    { id: '21', label: '9"x12"', type: 'send_postcard_9x12' },
  ];

  const rplOptions = [
    { id: '16', label: 'Personal Letter', type: 'send_personal_real_penned_letter' },
    { id: '38', label: 'Professional Letter', type: 'send_professional_real_penned_letter' },
  ];

  const professionalOptions = [
    { id: '2', label: 'Windowed Envelope', type: 'send_professional_letter_windowed' },
    { id: '4', label: 'Non-Windowed Envelope', type: 'send_professional_letter_non_windowed' },
  ];

  const selfMailerOptions = [
    { id: '9', label: 'Bi-Fold Self-Mailers', type: 'send_bi-fold_self-mailers' },
    { id: '11', label: 'Tri-Fold Self-Mailers', type: 'send_tri-fold_self_Mailers' },
  ];

  useEffect(() => {
    if (selectedPostCardOption) {
      setSelectedActionType(selectedPostCardOption);
    }
  }, [selectedPostCardOption]);

  useEffect(() => {
    if (selectedRplOption) {
      setSelectedActionType(selectedRplOption.type);
    } else if (selectedActionType?.includes('real_penned_letter') && !selectedRplOption) {
      setSelectedTemplate(null);
    }
  }, [selectedRplOption]);

  useEffect(() => {
    if (selectedProfessionalOption) {
      setSelectedActionType(selectedProfessionalOption.type);
    } else if (selectedActionType?.includes('professional_letter') && !selectedProfessionalOption) {
      setSelectedTemplate(null);
    }
  }, [selectedProfessionalOption]);

  useEffect(() => {
    if (selectedSelfMailerOption) {
      setSelectedActionType(selectedSelfMailerOption.type);
    } else if (selectedActionType?.includes('fold_self') && !selectedSelfMailerOption) {
      setSelectedTemplate(null);
    }
  }, [selectedSelfMailerOption]);

  useEffect(() => {
    setTimeout(() => {
      if (editingStep) {
        if (editingStep.action.includes('postcard')) {
          setSelectedPostCardOption(editingStep.action);
        } else if (editingStep.action.includes('real_penned_letter')) {
          setSelectedRplOption(rplOptions.find((option) => option.type === editingStep.action));
        } else if (editingStep.action.includes('professional_letter')) {
          setSelectedProfessionalOption(
            professionalOptions.find((option) => option.type === editingStep.action)
          );
        } else if (editingStep.action.includes('fold_self')) {
          setSelectedSelfMailerOptionOption(
            selfMailerOptions.find((option) => option.type === editingStep.action)
          );
        }
      }
    }, 10);
  }, [editingStep]);

  useEffect(() => {
    setErrors({});
    setSelectedPostCardOption(null);
    setSelectedRplOption(null);
    setSelectedProfessionalOption(null);
    setSelectedSelfMailerOptionOption(null);
  }, [open]);

  // Validation function to check if all required fields are filled
  const isFormValid = () => {
    if (!selectedActionType) {
      return false;
    }

    // Check if specific option is selected based on action type
    if (selectedActionType?.includes('postcard') && !selectedPostCardOption) {
      return false;
    }
    if (selectedActionType?.includes('real_penned_letter') && !selectedRplOption) {
      return false;
    }
    if (selectedActionType?.includes('professional_letter') && !selectedProfessionalOption) {
      return false;
    }
    if (selectedActionType?.includes('fold_self') && !selectedSelfMailerOption) {
      return false;
    }

    // Template is always required
    if (!selectedTemplate) {
      return false;
    }

    // Real penned letters have fixed postage (First Class Forever), so postage is not required.
    // Other products require backend postage details before they can be saved.
    const isRealPenned = selectedActionType?.includes('real_penned_letter');
    const isPostageNotRequired = isRealPenned || postageOptions.length === 1;
    const isSelectedPostageValid =
      selectedPostage &&
      postageOptions.some((postage) => postage?.value === selectedPostage?.value);

    if (
      (!isRealPenned && postageOptions.length === 0) ||
      (!isPostageNotRequired && !isSelectedPostageValid)
    ) {
      return false;
    }

    // Envelope is required if not a multi-page template
    if (!multiPageTemplates.includes(selectedTemplate?.productType) && !selectedEnvelope) {
      return false;
    }

    // Offer percentage is required if rosEnabled
    if (selectedTemplate?.meta?.rosEnabled === true && !offerPercentageValue) {
      return false;
    }

    return true;
  };

  return (
    <OlcFormModal
      onClose={onClose}
      open={open}
      title={!selectedActionType ? 'Add Mail Action' : 'Choose Product Options'}
      subtitle={!selectedActionType ? 'Choose what type of mail to send' : undefined}
      className={selectedActionType ? 'productOptionsModal' : 'productOptionsModal actionsModal'}
      footerClassName="campaignMailActionFooter"
      footer={
        <>
          <OlcButton variant="secondary" onClick={onClose}>
            Cancel
          </OlcButton>
          <OlcButton
            variant="primary"
            disabled={Boolean(addingActionLoading || (selectedActionType && !isFormValid()))}
            onClick={handleSaveAction}
          >
            {selectedActionType ? 'Add Action' : 'Next'}
          </OlcButton>
        </>
      }
    >
      <Box
        className={!selectedActionType ? 'actionDialogContent' : 'productOptionsModalContent'}
      >
        {!selectedActionType && (
          <>
            <Grid container spacing="12px">
              {(() => {
                // Custom sort order by product IDs
                const sortOrder = [13, 9, 5, 18, 2, 16]; // Postcards, Self Mailer, Personal Letter, Snap Pack, Professional Letter, Real Penned Letter

                // Filter first
                const filteredActions = COMPAIGNS_ACTION_TYPE.filter(
                  (item) => ![4, 14, 15, 21, 11, 38].includes(+item.productId)
                );

                // Sort by custom order
                return filteredActions.sort((a, b) => {
                  const indexA = sortOrder.indexOf(a.productId);
                  const indexB = sortOrder.indexOf(b.productId);

                  // If both are in sort order, sort by their position
                  if (indexA !== -1 && indexB !== -1) {
                    return indexA - indexB;
                  }
                  // If only A is in sort order, A comes first
                  if (indexA !== -1) return -1;
                  // If only B is in sort order, B comes first
                  if (indexB !== -1) return 1;
                  // If neither is in sort order, keep original order
                  return 0;
                });
              })().map((action) => (
                <Grid item xs={12} sm={12} md={6} lg={6} key={action.type}>
                  <Card
                    className={`mailActionTypeCard${
                      previouslySelectedActionType === action.type ? ' isSelected' : ''
                    }`}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      paddingLeft: '20px',
                      height: '76px',
                      border:
                        previouslySelectedActionType === action.type
                          ? '1px solid rgba(237, 92, 47, 0.4)'
                          : '0.5px solid rgba(0, 0, 0, 0.16)',
                      boxShadow: '2px 2px 4px 0px rgba(0, 0, 0, 0.04)',
                      transition: 'all 0.2s ease',
                      backgroundColor:
                        previouslySelectedActionType === action.type && !selectedActionType
                          ? 'rgba(237, 92, 47, 0.08)'
                          : '',
                    }}
                    onClick={() => {
                      setSelectedActionType(action.type);
                      // If re-selecting the same product, restore the previous template
                      if (
                        action.type === previouslySelectedActionType &&
                        previouslySelectedTemplate
                      ) {
                        setSelectedTemplate(previouslySelectedTemplate);
                      } else {
                        // If selecting a different product, clear template selection
                        setSelectedTemplate(null);
                      }
                    }}
                  >
                    <CardContent sx={{ p: 0, '&:last-child': { padding: 0 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                        <Avatar
                          sx={{
                            backgroundColor: 'transparent',
                            width: 60,
                            height: 60,
                            mr: 2,
                          }}
                        >
                          <img
                            src={action.icon}
                            alt={action.label}
                            style={{ width: '44px', height: '44px' }}
                          />
                        </Avatar>
                        <Box>
                          <Box>
                            <Typography
                              variant="subtitle1"
                              fontWeight="500"
                              sx={{ fontFamily: 'Inter', color: '#000' }}
                            >
                              {action.newLabel}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {selectedActionType?.includes('postcard') && (
          <>
            <Box className="optionsWrapper">
              <Typography variant="h6">Product Options</Typography>
              <Box className="optionsList">
                {postcardOptions.map((option) => (
                  <Box key={option.id} className="optionItem">
                    <label className="radioLabel">
                      <input
                        type="radio"
                        name="productOption"
                        value={option.type}
                        checked={selectedPostCardOption === option.type}
                        onChange={(e) => setSelectedPostCardOption(e.target.value)}
                        className="radioInput"
                      />
                      <span className="radioCircle"></span>
                      <Typography variant="body1">{option.label}</Typography>
                    </label>
                  </Box>
                ))}
              </Box>
            </Box>
            <Divider className="divider" />
          </>
        )}
        {selectedActionType?.includes('professional_letter') && (
          <Box className="selectTemplate">
            <Typography variant="h6">
              {MESSAGES.ORDERS.CREATE.ENVELOPE_TYPE_LABEL}
              <span className="olcRequired">*</span>
            </Typography>
            <GenericAutocomplete
              placeholder={MESSAGES.ORDERS.CREATE.ENVELOPE_TYPE_LABEL}
              options={professionalOptions}
              setSelectedValue={setSelectedProfessionalOption}
              selectedValue={selectedProfessionalOption}
              updateErrors={setErrors}
              campaign={true}
              search={() => {}}
              className={'newAutocompleteDesign'}
              newDesign={true}
            />
          </Box>
        )}
        {selectedActionType?.includes('fold_self') && (
          <Box className="selectTemplate">
            <Typography variant="h6">
              {MESSAGES.ORDERS.CREATE.DESIGN_FORMAT_LABEL}
              <span className="olcRequired">*</span>
            </Typography>
            <GenericAutocomplete
              placeholder={MESSAGES.ORDERS.CREATE.DESIGN_FORMAT_LABEL}
              options={selfMailerOptions}
              setSelectedValue={setSelectedSelfMailerOptionOption}
              selectedValue={selectedSelfMailerOption}
              updateErrors={setErrors}
              campaign={true}
              search={() => {}}
              className={'newAutocompleteDesign'}
              newDesign={true}
            />
          </Box>
        )}
        {selectedActionType?.includes('real_penned_letter') && (
          <Box className="selectTemplate">
            <Typography variant="h6">
              {MESSAGES.ORDERS.CREATE.DESIGN_FORMAT_LABEL}
              <span className="olcRequired">*</span>
            </Typography>
            <GenericAutocomplete
              placeholder="Design Format"
              options={rplOptions}
              setSelectedValue={setSelectedRplOption}
              selectedValue={selectedRplOption}
              updateErrors={setErrors}
              campaign={true}
              search={() => {}}
              className={'newAutocompleteDesign'}
              newDesign={true}
            />
          </Box>
        )}

        {selectedTemplate?.envelopeType && (
          <Box className="selectTemplate" style={{ marginTop: '-25px' }}>
            <Typography className="postageType" style={{ marginBottom: '15px' }}>
              {MESSAGES.ORDERS.CREATE.ENVELOPE_TYPE_LABEL} : {selectedTemplate?.envelopeType}
            </Typography>
            <Divider className="divider" />
          </Box>
        )}

        {selectedActionType && (
          <Box className="selectTemplate">
            {/* Template Selection */}
            <Typography
              variant="h6"
              className={
                (selectedActionType?.includes('postcard') && !selectedPostCardOption) ||
                (selectedActionType?.includes('real_penned_letter') && !selectedRplOption) ||
                (selectedActionType?.includes('professional_letter') && !selectedProfessionalOption)
                  ? 'disable'
                  : ''
              }
            >
              {MESSAGES.ORDERS.CREATE.TEMPLATE_LABEL}
              <span className="olcRequired">*</span>
            </Typography>
            <OlcDropdownSearch
              options={templateDropdownOptions}
              selectedValue={selectedTemplateDropdownValue}
              setSelectedValue={handleTemplateSelection}
              component="multipleIds"
              type="templates"
              clearable
              placeHolderText="Select Template"
              searchFieldPlaceholderText="Template"
              noOptionText="No templates found"
              onSearchChange={setSearch}
              searchLoading={loading}
              disabled={
                (selectedActionType?.includes('postcard') && !selectedPostCardOption) ||
                (selectedActionType?.includes('real_penned_letter') && !selectedRplOption) ||
                (selectedActionType?.includes('professional_letter') && !selectedProfessionalOption)
              }
              error={Boolean(errors.template)}
              className="campaignTemplateDropdown"
            />
            {errors.template && (
              <Typography className="errorMessage">
                <sup>*</sup>
                {MESSAGES.ORDERS.CREATE.TEMPLATE_REQUIRED}
              </Typography>
            )}

            {/* Postage Type */}
            {selectedTemplate && selectedActionType?.includes('real_penned_letter') ? (
              <>
                <Typography className="postageType">
                  Postage Type: First Class Forever (1-5 business days)
                </Typography>
              </>
            ) : selectedTemplate && postageOptions.length === 1 ? (
              <>
                <Typography className="postageType">
                  Postage Type: {postageOptions[0]?.label}
                </Typography>
              </>
            ) : (
              <>
                {selectedTemplate && (
                  <Box sx={{ marginTop: '19px' }}>
                    <Typography variant="h6" className={!selectedTemplate ? 'disable' : ''}>
                      {MESSAGES.ORDERS.CREATE.POSTAGE_TYPE_LABEL}
                      <span className="olcRequired">*</span>
                    </Typography>
                    <GenericAutocomplete
                      placeholder="Postage Type"
                      options={postageOptions}
                      setSelectedValue={setSelectedPostage}
                      disable={!selectedTemplate || postageOptions.length <= 1}
                      selectedValue={selectedPostage}
                      search={() => {}}
                      updateErrors={setErrors}
                      name={'postage'}
                      campaign={true}
                      className={'newAutocompleteDesign'}
                      newDesign={true}
                      readOnlyInput={true}
                    />
                    {errors.postage && (
                      <Typography className="errorMessage">
                        <sup>*</sup>
                        {MESSAGES.ORDERS.CREATE.POSTAGE_TYPE_REQUIRED}
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}

            {/* {isUvCoatingVisible && (
              <Box className="uvCoatingControl">
                <Box className="uvCoatingCopy">
                  <Typography variant="h6" className="uvCoatingLabel">
                    Add UV coating
                  </Typography>
                  {uvCoatingPriceLoading ? (
                    <CircularProgress className="uvCoatingPriceLoader" size={14} />
                  ) : uvCoatingPrice != null && (
                    <Typography className="uvCoatingPriceBadge">
                      +{balanceFormatter(uvCoatingPrice)}/pc
                    </Typography>
                  )}
                  <Tooltip
                    disableInteractive
                    title="A high-gloss UV coating on the front for a premium, eye-catching finish that resists scuffing in the mail."
                    placement="top"
                  >
                    <button
                      type="button"
                      className="uvCoatingInfoButton"
                      aria-label="Learn more about UV coating"
                    >
                      <img src={InfoCIcon} alt="" aria-hidden="true" />
                    </button>
                  </Tooltip>
                </Box>
                <button
                  type="button"
                  className={`uvCoatingToggleWrapper ${uvCoating ? 'enabled' : ''}`}
                  onClick={handleUvCoatingToggle}
                  role="switch"
                  aria-checked={uvCoating}
                  aria-label="Add UV coating"
                >
                  <span className="toggle-track">
                    <span className="toggle-thumb" />
                  </span>
                </button>
              </Box>
            )} */}

            {/* Envelope Style */}
            {selectedTemplate && !multiPageTemplates.includes(selectedTemplate?.productType) && (
              <Box sx={{ marginTop: '19px' }}>
                <Typography variant="h6" className={!selectedTemplate ? 'disable' : ''}>
                  {MESSAGES.ORDERS.CREATE.ENVELOPE_STYLE_LABEL}
                  <span className="olcRequired">*</span>
                </Typography>
                <GenericAutocomplete
                  placeholder="Envelope Style"
                  options={evelopeOptions}
                  disable={!selectedTemplate}
                  setSelectedValue={setSelectedEnvelope}
                  selectedValue={selectedEnvelope}
                  search={() => {}}
                  updateErrors={setErrors}
                  name={'envelope'}
                  campaign={true}
                  className={'newAutocompleteDesign'}
                  newDesign={true}
                  listClass="campaignAutocompleteDropDown"
                />
                {errors.envelope && (
                  <Typography className="errorMessage">
                    <sup>*</sup>
                    {MESSAGES.ORDERS.CREATE.ENVELOPE_STYLE_REQUIRED}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {selectedTemplate?.id && selectedTemplate?.meta?.rosEnabled === true && (
          <Box className="selectTemplate">
            <Typography variant="h6">
              Enter Offer Percentage <span className="olcRequired">*</span>
            </Typography>
            <Input
              type="number"
              placeholder={MESSAGES.ORDERS.CREATE.OFFER_PERCENTAGE_PLACEHOLDER}
              className="offerPerInput"
              value={offerPercentageValue}
              onChange={(e) => handleOfferPercentageChange(e)}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              endAdornment={
                <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                  {offerPercentageValue && (
                    <IconButton
                      className="cpClearInputBtn smallClearIcon"
                      size="small"
                      onClick={() => {
                        setOfferPercentageValue('');
                        setErrors((prev) => {
                          const { offerPercentage, ...rest } = prev || {};
                          return rest;
                        });
                      }}
                      edge="end"
                      sx={{ padding: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              }
            />
            {errors.offerPercentage && !offerPercentageValue && (
              <Typography className="errorMessage">
                <sup>*</sup>
                {MESSAGES.ORDERS.CREATE.OFFER_PERCENTAGE_REQUIRED}
              </Typography>
            )}
            <Typography className="clickHere">
              {MESSAGES.ORDERS.CREATE.OFFER_PERCENTAGE_DESCRIPTION}{' '}
              <span>
                <NavLink to="/property-offers" target="_blank">
                  {MESSAGES.ORDERS.CREATE.OFFER_PERCENTAGE_LINK}
                </NavLink>
              </span>
            </Typography>
          </Box>
        )}
      </Box>
    </OlcFormModal>
  );
};

export default ActionsModal;
